// Helpers for embedded (inline) email images inside ticket bodies.

// Stable short key for an image src (djb2 hash) — used to persist per-ticket overrides.
export function imageKey(src) {
  let h = 5381;
  const s = src || '';
  // hash a bounded slice for speed; include length to reduce collisions
  const slice = s.slice(0, 4000);
  for (let i = 0; i < slice.length; i++) {
    h = ((h << 5) + h + slice.charCodeAt(i)) & 0xffffffff;
  }
  return 'img_' + (h >>> 0).toString(16) + '_' + s.length;
}

// Images at or below this decoded byte size are treated as logos/signatures
// (collapsed in the embedded-images panel so real screenshots surface first).
export const SIGNATURE_MAX_BYTES = 12 * 1024;

// Extract embedded data-URI images from one or more HTML strings.
// Skips SVG placeholders (used for unresolved images). Captures the img `alt`
// (often the original filename, e.g. image001.png) as `name` for captions.
export function extractInlineImages(htmls = []) {
  const list = Array.isArray(htmls) ? htmls : [htmls];
  const seen = new Set();
  const images = [];
  const tagRe = /<img\b[^>]*>/gi;
  const srcRe = /\bsrc\s*=\s*["'](data:image\/[^;"']+;base64,[^"']+)["']/i;
  const altRe = /\balt\s*=\s*["']([^"']*)["']/i;
  for (const html of list) {
    if (!html) continue;
    let tag;
    while ((tag = tagRe.exec(html)) !== null) {
      const srcMatch = srcRe.exec(tag[0]);
      if (!srcMatch) continue;
      const src = srcMatch[1];
      if (src.startsWith('data:image/svg')) continue; // placeholder, not a real image
      const key = src.length + ':' + src.slice(0, 160);
      if (seen.has(key)) continue;
      seen.add(key);
      const mimeMatch = /^data:(image\/[^;]+);/.exec(src);
      const mime = mimeMatch ? mimeMatch[1] : 'image/png';
      const ext = (mime.split('/')[1] || 'png').replace('jpeg', 'jpg');
      const altMatch = altRe.exec(tag[0]);
      const alt = altMatch ? altMatch[1].trim() : '';
      const size = estimateDataUriSize(src);
      images.push({ src, mime, ext, size, name: alt, key: imageKey(src), isLikelySignature: size <= SIGNATURE_MAX_BYTES });
    }
  }
  return images.map((img, i) => ({
    ...img,
    index: i + 1,
    name: img.name || `Image ${i + 1}.${img.ext}`,
  }));
}

// Approximate decoded byte size of a base64 data URI.
export function estimateDataUriSize(src) {
  const b64 = (src.split(',')[1] || '');
  const padding = (b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0);
  return Math.max(0, Math.floor((b64.length * 3) / 4) - padding);
}

export function downloadInlineImage(src, filename) {
  const a = document.createElement('a');
  a.href = src;
  a.download = filename || `image-${Date.now()}.png`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

// True if any provided HTML still contains an unresolved cid: image reference.
export function hasUnresolvedImages(htmls = []) {
  const list = Array.isArray(htmls) ? htmls : [htmls];
  return list.some((h) => h && /<img[^>]+src\s*=\s*["']cid:/i.test(h));
}
